import React, { useState } from 'react';
import { ClockIcon, MapPinIcon, TicketIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Meter } from '../ui/Meter';
import { ChoiceGroup } from '../ui/ChoiceGroup';
import { EmptyState } from '../ui/EmptyState';
import { ReportControls } from '../trust/ReportControls';
import { events as seedEvents } from '../../data/campus';
import { naira, percent } from '../../utils/format';
import type { CampusEvent } from '../../types/campus';
const FILTERS = ['All', 'Academic', 'Career', 'Sports', 'Social', 'Association'] as const;
export function EventsBoard() {
  const [events, setEvents] = useState<CampusEvent[]>(seedEvents);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('All');
  const visible = events.filter(event => filter === 'All' || event.category === filter);
  const myEvents = events.filter(event => event.registeredByMe);
  function toggleRegistration(id: string) {
    setEvents(current => current.map(event => event.id === id ? {
      ...event,
      registeredByMe: !event.registeredByMe,
      registered: event.registered + (event.registeredByMe ? -1 : 1)
    } : event));
  }
  return <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-874" data-mp="utnno7nyzp76">
      <div className="space-y-4" data-id="element-875" data-mp="pvcdtwxodeoy">
        <ChoiceGroup options={FILTERS} value={filter} onChange={setFilter} size="sm" data-id="element-876" data-mp="pwe0kyo1zpmi" />

        {visible.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-877" data-mp="l3kvg9ooaf9w">
            <EmptyState icon={<ClockIcon className="h-4 w-4" data-id="element-879" data-mp="g3q4y78t7rpd" />} title="No events in this category" description="Nothing is scheduled under this category yet. Try another filter." data-id="element-878" data-mp="g6kolwk018hq" />
          </div> : <ul className="space-y-3" data-id="element-880" data-mp="269ywwufdzy5">
            {visible.map(event => {
          const fill = percent(event.registered, event.capacity);
          return <li key={event.id} className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-881" data-mp="3opwdxwgdg9fd">
                  <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-882" data-mp="3sn2a9w7xjbw6">
                    <div className="min-w-0" data-id="element-883" data-mp="3ergr45odnmbo">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-884" data-mp="3jo73oj0z3zsc">
                        <h3 className="text-[15px] font-semibold text-ink" data-id="element-885" data-mp="b8x53kwalcpb">{event.title}</h3>
                        <Badge data-id="element-886" data-mp="bb9mnwy51l1u">{event.category}</Badge>
                        {event.ticket === 'Paid' && event.price && <Badge tone="brand" icon={<TicketIcon className="h-3.5 w-3.5" data-id="element-888" data-mp="1iaw49j2zkfl" />} data-id="element-887" data-mp="1jm12co9iy8a">
                            {naira(event.price)}
                          </Badge>}
                      </div>
                      <p className="mt-1 text-[13px] text-ink-muted" data-id="element-889" data-mp="u6zonk62mhfq">{event.organizer}</p>
                      <p className="cz-num mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-ink-muted" data-id="element-890" data-mp="yzjcd9yh2sy9">
                        <span className="inline-flex items-center gap-1.5" data-id="element-891" data-mp="2jnc1ssqj5r7o">
                          <ClockIcon className="h-3.5 w-3.5" data-id="element-892" data-mp="2eor1yuf6x9jr" />
                          {event.dateLabel}, {event.time}
                        </span>
                        <span className="inline-flex items-center gap-1.5" data-id="element-893" data-mp="2sj1hcl5arrd5">
                          <MapPinIcon className="h-3.5 w-3.5" data-id="element-894" data-mp="2ogevr1z7z2oe" />
                          {event.venue}
                        </span>
                      </p>
                    </div>
                    <div className="w-full shrink-0 sm:w-44" data-id="element-895" data-mp="1vsir9b3es6h2">
                      <Meter value={fill} label="Registered" trailing={`${event.registered}/${event.capacity}`} tone={fill > 90 ? 'warn' : 'brand'} data-id="element-896" data-mp="29r8pwv5vme7j" />
                      <Button size="sm" variant={event.registeredByMe ? 'secondary' : 'primary'} className="mt-3 w-full" onClick={() => toggleRegistration(event.id)} data-id="element-897" data-mp="3l7p82w8tf8y3">
                        {event.registeredByMe ? 'Cancel registration' : event.ticket === 'Paid' ? 'Get ticket' : 'Register'}
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-line pt-3" data-id="element-898" data-mp="y8lhtnzmmoif">
                    <p className="text-[12px] text-ink-subtle" data-id="element-899" data-mp="180947ahbtfd1">
                      {event.ticket === 'Paid' ? 'Tickets are marketplace orders — payment is handled by a licensed provider.' : 'Free entry — registration reserves your place.'}
                    </p>
                    <ReportControls subject={event.title} surface="event" data-id="element-900" data-mp="1lzqvpxm0b6q3" />
                  </div>
                </li>;
        })}
          </ul>}
      </div>

      <Panel title="Your registrations" bodyClassName="p-0" data-id="element-901" data-mp="32gh99jx7kq46">
        {myEvents.length === 0 ? <p className="px-5 py-4 text-[13px] text-ink-muted" data-id="element-902" data-mp="2xhd18wm0syff">
            You have not registered for anything yet.
          </p> : <ul className="divide-y divide-line" data-id="element-903" data-mp="2sjbi6bolgz3y">
            {myEvents.map(event => <li key={event.id} className="px-5 py-3" data-id="element-904" data-mp="3q6bm41hj2eof">
                <p className="text-[13px] font-medium text-ink" data-id="element-905" data-mp="3q7v0tnnjcho3">{event.title}</p>
                <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-906" data-mp="3l7qtan4luxa0">
                  {event.dateLabel}, {event.time}
                </p>
              </li>)}
          </ul>}
      </Panel>
    </div>;
}